import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Form, Popup } from 'semantic-ui-react'
import CustomIcon from '~/components/CustomIcon'
import Dialog from '~/components/Dialog'
import { IndividualPhaseWrapper, StyledDivider } from './style'
import CustomInput from '~/components/CustomInput'

interface Props {
  isOpenIndPhasesModal: boolean
  handleClose: () => void
  rearingPhases: any[]
  handleSubmit: (rearingPhases: any[]) => void
}

const HeiferIndividualEdit: React.FC<Props> = (props) => {
  const { control } = useFormContext()
  const { isOpenIndPhasesModal, handleClose, handleSubmit } = props
  const { t } = useTranslation()

  const [rearingPhases, setRearingPhases] = useState([])

  const watchFirstCalvingAge = useWatch({ control, name: 'firstCalvingAge' })

  useEffect(() => {
    if (isOpenIndPhasesModal) {
      setRearingPhases(props.rearingPhases)
    }
  }, [props.rearingPhases, isOpenIndPhasesModal])

  function addNewIndividualPhase() {
    const tempPhases = [...rearingPhases]
    tempPhases.splice(
      rearingPhases.length - 1,
      0,
      {
        phase: 2,
        days: 0,
        name: '',
        period: '3-EKA',
        fixed: false,
        concentratedFeeds: [],
        coarseFeeds: []
      })
    setRearingPhases(tempPhases)
  }

  function totalDaysSum() {
    return rearingPhases.reduce((previousValue, currentValue) => {
      return parseInt(previousValue) + parseInt(currentValue.days || 0)
    }, 0)
  }

  function renderCustomPhases() {
    const transformedEka = parseFloat(watchFirstCalvingAge?.toString()?.replace(',', '.'))
    return <IndividualPhaseWrapper>
      <div style={{ width: 'fit-content' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: '-0.5em' }}>
          {'Aufzuchtphasen'}
          <Popup
            content={'Weitere Aufzuchtphase hinzufügen'}
            trigger={<Button
              icon='plus'
              disabled={rearingPhases.length === 5}
              type='button'
              style={{ color: 'white', fontSize: '0.9em', marginTop: 12 }}
              onClick={() => addNewIndividualPhase()}
            />}
          />
        </div>
        <StyledDivider />
      </div>
      <Form>
        {rearingPhases.map((individualPhase, index) => {
          const disabled = index === 0 || index === rearingPhases.length - 1
          return <Form.Group
            key={individualPhase.key}>
            <div className='phase-counter'>{index + 1}.</div>
            <Form.Input
              label={'Bezeichnung'}
              value={individualPhase?.name}
              placeholder={'Name der Aufzuchtphase'}
              onChange={(_, { value }) => {
                const tempPhases = [...rearingPhases]
                tempPhases[index].name = value
                setRearingPhases(tempPhases)
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 30, marginTop: 24 }}>
              <CustomInput
                disabled={disabled}
                value={individualPhase?.days}
                suffix={' Tage'}
                placeholder={t('Enter days')}
                onChange={(value) => {
                  const tempPhases = [...rearingPhases]
                  tempPhases[index].days = parseInt(value || 0)
                  const lastPhase = tempPhases[tempPhases.length - 1]
                  lastPhase.days = tempPhases.reduce((previousValue, currentValue, index) => {
                    if (index < tempPhases.length - 1 && index !== 0) {
                      return parseInt(previousValue) - parseInt(currentValue.days || 0)
                    }
                    return previousValue
                  }, Math.round((transformedEka * 30.5 - 63 - 28) * 10) / 10)
                  setRearingPhases(tempPhases)
                }}
              />
              {!disabled && <CustomIcon onClick={() => {
                const tempPhases = rearingPhases.filter((_, indexPhase) => indexPhase !== index)
                const lastPhase = tempPhases[tempPhases.length - 1]
                lastPhase.days = tempPhases.reduce((previousValue, currentValue, index) => {
                  if (index < tempPhases.length - 1 && index !== 0) {
                    return parseInt(previousValue) - parseInt(currentValue.days)
                  }
                  return previousValue
                }, Math.round((transformedEka * 30.5 - 63 - 28) * 10) / 10)
                setRearingPhases(tempPhases)
              }} size='large' name='trash' />
              }
            </div>
          </Form.Group>
        })}
      </Form>
      <StyledDivider />
      <div style={{ display: 'flex', fontWeight: 'bold', fontStyle: 'italic' }}>
        <div style={{ width: 365 }}>{'Haltungstage (gesamt): '}</div>
        <div>{`${totalDaysSum()} Tage`}</div>
      </div>
    </IndividualPhaseWrapper>
  }

  const hasError = rearingPhases.findIndex(rearingPhase => !rearingPhase.name || !rearingPhase.days) >= 0

  return <Dialog
    isDialogOpen={isOpenIndPhasesModal}
    style={{ width: 800 }}
    textHeader={'Festlegung der Aufzuchtphasen für Färsen'}
    textClose={t('Close')}
    disableSubmit={hasError}
    textSubmit={'Aufzuchtphasen aktualisieren'}
    textContent={renderCustomPhases()}
    handleClose={() => handleClose()}
    handleSubmit={() => handleSubmit(rearingPhases)}
  />
}
export default HeiferIndividualEdit